import type { Store } from '@reduxjs/toolkit';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import { getUniversalUrl } from 'app/lib/get-universal-url';
import { getGaClientId, getGaSessionId } from 'app/packages/analytics';
import { webSockets } from 'app/packages/lib';

import { signalrConnected, signalrDisconnected } from '../redux/actions/connection';
import mainHubInstance from './main-hub';
import startCallBackWithDelay from './utils/start-callback-with-delay';

// ---

const SERVER_TIMEOUT = 100000;

export function initializeWebSockets(store: Store<ReduxState>) {
  const featureFlags = store.getState()?.siteConfigs?.featureFlags;
  const isBackendApiV2 = featureFlags ? featureFlags.includes(FeatureFlag.MESSAGE_PACK) : false;

  const hubInstance = new webSockets.HubBuilder(
    getUniversalUrl('/signalr'),
    {
      headers: {
        'X-Web-Client-Id': getGaClientId(),
        'X-Session-Id': getGaSessionId(),
      },
    },
    isBackendApiV2,
  );

  mainHubInstance.initialize(hubInstance.connection, isBackendApiV2);
  hubInstance.connection.serverTimeoutInMilliseconds = SERVER_TIMEOUT;
  hubInstance.connection.onclose((error?: Error) => stop(store, hubInstance, error));

  return startCallBackWithDelay(() => connect(store, hubInstance));
}

// ---

async function connect(store: Store<ReduxState>, hubInstance: webSockets.HubBuilder) {
  await hubInstance.connect();
  return store.dispatch(signalrConnected());
}

function stop(store: Store<ReduxState>, hubInstance: webSockets.HubBuilder, error?: Error) {
  if (error) {
    console.error('initializeWebSockets -> socket was closed with error = ', error);
  }

  store.dispatch(signalrDisconnected());
  return startCallBackWithDelay(() => connect(store, hubInstance));
}

export function reconnect() {
  return mainHubInstance.tryToGetHub().stop();
}
